<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import Alert from '@/components/Atoms/Alert.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCamera from '@/components/icon/IconCamera.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconLock from '@/components/icon/IconLock.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import CaptionService from '@/services/CaptionService'
import SyncWordsSettingsFields from '@/components/Captioning/SyncWordsSettingsFields.vue'
import { ENGINE_SYNCWORDS, draftFromFlow, settingsFromDraft, validateDraft, mapServerErrors, providerUsageLabel, unsupportedLanguageErrors } from '@/components/Captioning/captionSyncWords'
import StreamStatsService from '@/services/StreamStatsService'
import { isLimitError, limitErrorCopy, UPGRADE_ROUTE, DEFAULT_LIMITS } from '@/components/Captioning/captionLimits'
import { track, EVENTS } from '@/lib/tracking'

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	// Stream keys that already have a captioning flow; those rows are disabled.
	takenStreamKeys: { type: Array, default: () => [] },
	// Plan limits from the list: `maxLangs` caps a SyncWords flow's languages.
	limits: { type: Object, default: () => ({ ...DEFAULT_LIMITS }) },
})

const emit = defineEmits(['update:modelValue', 'created'])

const store = useStore()
const { notify } = useNotification()

const isVisible = computed({
	get: () => props.modelValue,
	set: (v) => emit('update:modelValue', v),
})

const search = ref('')
const selectedKey = ref('')
const submitting = ref(false)
const error = ref('')
// A plan-limit error offers the plans page next to the message.
const errorUpgrade = ref(false)
const liveStatus = ref({})
const loadingStreams = ref(false)

// SyncWords engine (GET /captions/engine): the stream, then its caption
// settings, then Create. Castr keeps the single stream step.
const engineInfo = ref(null)
const step = ref('stream')
const swDraft = ref(null)
// Core's field errors, kept with the value they were raised on.
const swServerErrors = ref({})
// Region core picks for the chosen stream when the create sends none; '' until known.
const streamRegion = ref('')
const syncwordsEngine = computed(() => engineInfo.value?.engine === ENGINE_SYNCWORDS)
const swUnavailable = computed(() => syncwordsEngine.value && engineInfo.value?.syncwords?.configured === false)
// On the account's own SyncWords keys Castr's plan doesn't cap languages; the service's 4 does.
const ownAccount = computed(() => !!engineInfo.value?.provider?.connected)
// The linked account's usage this month (GET /captions/provider), shown with the account line.
const providerUsage = ref(null)
const providerUsageText = computed(() => (ownAccount.value ? providerUsageLabel(providerUsage.value) : ''))
const swMaxLangs = computed(() => {
	if (ownAccount.value) return DEFAULT_LIMITS.maxLangs
	return Number.isInteger(props.limits?.maxLangs) && props.limits.maxLangs > 0 ? props.limits.maxLangs : DEFAULT_LIMITS.maxLangs
})
const swPlanCapped = computed(() => !ownAccount.value && props.limits?.enforced === true && swMaxLangs.value < DEFAULT_LIMITS.maxLangs)
const accountLine = computed(() => (ownAccount.value
	? `Runs on your connected captioning account${engineInfo.value.provider.account?.name ? ` (${engineInfo.value.provider.account.name})` : ''}, billed by that account.`
	: 'Runs on Castr\'s captioning service, using your plan\'s captioning hours.'))
// The plan's language limit from core, shown under Caption languages.
const swLanguagesUpgrade = ref(false)
const swValidation = computed(() => (swDraft.value ? validateDraft(swDraft.value, engineInfo.value, { maxLangs: swMaxLangs.value }) : { valid: false, errors: {} }))
const swErrors = computed(() => {
	const out = {}
	for (const [field, e] of Object.entries(swServerErrors.value)) {
		if (swDraft.value?.[field] === e.value) out[field] = e.message
	}
	return { ...out, ...swValidation.value.errors }
})

// Only `live` ("all in one") streams can be captioned: the captions worker pulls
// the stream's HLS at /<account>/<live_key>/. ipcam, sub-second, scheduled,
// restream, TV playout and VOD are routed differently and are never listed.
// Filtered here on `type` rather than through a store getter so the rule is
// explicit and cannot drift with the getter's semantics.
const CAPTIONABLE_TYPES = ['live']
const liveStreams = computed(() => (store.getters['Streams/streams'] || []).filter((s) => CAPTIONABLE_TYPES.includes(s?.type)))

const rows = computed(() => {
	const q = search.value.trim().toLowerCase()
	const taken = new Set(props.takenStreamKeys)
	return liveStreams.value
		.filter((s) => !q || (s.name || '').toLowerCase().includes(q) || (s.key || '').toLowerCase().includes(q))
		.map((s) => ({
			id: s._id,
			key: s.key,
			name: s.name,
			enabled: s.enabled !== false,
			online: !!liveStatus.value[s.key],
			taken: taken.has(s.key),
			// SyncWords pushes the captioned video into the stream: a pull-source stream can't take it.
			pull: syncwordsEngine.value && typeof s.pullUrl === 'string' && !!s.pullUrl.trim(),
		}))
		.sort((a, b) => Number(b.online) - Number(a.online) || (a.name || '').localeCompare(b.name || ''))
})

const selected = computed(() => rows.value.find((r) => r.key === selectedKey.value) || null)
// Waits for the engine so a SyncWords flow is never created without its settings step.
const canPick = computed(() => !!engineInfo.value && !!selected.value && !selected.value.taken && !selected.value.pull)
const canCreate = computed(() => {
	if (!canPick.value || submitting.value) return false
	if (!syncwordsEngine.value) return true
	return step.value === 'settings' && swValidation.value.valid && !swUnavailable.value
})

async function refreshPulse(streams) {
	await Promise.allSettled(streams.map(async (s) => {
		if (!s?.key) return
		try {
			const pulse = await StreamStatsService.getStreamMediaPulse(s.key)
			liveStatus.value = { ...liveStatus.value, [s.key]: !!pulse?.alive }
		} catch {
			liveStatus.value = { ...liveStatus.value, [s.key]: false }
		}
	}))
}

async function onOpen() {
	search.value = ''
	selectedKey.value = ''
	error.value = ''
	errorUpgrade.value = false
	step.value = 'stream'
	swServerErrors.value = {}
	loadingStreams.value = liveStreams.value.length === 0
	providerUsage.value = null
	CaptionService.getEngine().then((info) => {
		engineInfo.value = info
		swDraft.value = draftFromFlow(null, info)
		if (info.provider?.connected) {
			CaptionService.getProvider().then((p) => { providerUsage.value = p.providerUsage }).catch(() => {})
		}
	}).catch(() => {})
	try {
		await store.dispatch('Streams/getStreams')
	} catch {
		// The store keeps whatever it had; the empty state covers the rest.
	} finally {
		loadingStreams.value = false
	}
	refreshPulse(liveStreams.value)
}

watch(isVisible, (open) => {
	if (open) onOpen()
})

function choose(row) {
	if (row.taken || row.pull) return
	selectedKey.value = row.key
	error.value = ''
}

function close() {
	isVisible.value = false
}

function next() {
	if (!canPick.value) return
	error.value = ''
	step.value = 'settings'
	loadStreamRegion(selected.value.key)
}

/** Names Automatic after the region nearest the stream; a failure keeps the generic label. */
async function loadStreamRegion(streamKey) {
	streamRegion.value = ''
	try {
		const info = await CaptionService.getEngineForStream(streamKey)
		if (selectedKey.value === streamKey) streamRegion.value = info.stream?.region || ''
	} catch {
		// The generic "Automatic (nearest to your stream)" stays.
	}
}

function back() {
	error.value = ''
	step.value = 'stream'
}

async function create() {
	if (!canCreate.value) return
	submitting.value = true
	error.value = ''
	const syncwords = syncwordsEngine.value
	try {
		const flow = syncwords
			? await CaptionService.createFlow(selected.value.key, { syncwords: settingsFromDraft(swDraft.value) })
			: await CaptionService.createFlow(selected.value.key)
		track(EVENTS.CAPTIONING_CREATED, { streamId: selected.value.id, engine: flow?.engine })
		notify({
			group: 'success',
			text: syncwords
				? 'Captioning created. Start captions, then start your encoder on the SRT ingest.'
				: 'Captioning flow created. Choose the languages to start.',
		})
		emit('created', flow)
		isVisible.value = false
	} catch (err) {
		errorUpgrade.value = false
		swLanguagesUpgrade.value = false
		if (syncwords && err?.code === 'captions_invalid_settings' && Array.isArray(err.errors)) {
			const { fields, general } = mapServerErrors(err.errors)
			swServerErrors.value = Object.fromEntries(Object.entries(fields).map(([field, message]) => [field, { message, value: swDraft.value[field] }]))
			error.value = ['Some settings need fixing.', ...general].join(' ')
		} else if (syncwords && unsupportedLanguageErrors(err, swDraft.value)) {
			const fields = unsupportedLanguageErrors(err, swDraft.value)
			swServerErrors.value = Object.fromEntries(Object.entries(fields).map(([field, message]) => [field, { message, value: swDraft.value[field] }]))
		} else if (syncwords && err?.code === 'captions_too_many_languages') {
			const copy = limitErrorCopy(err)
			swServerErrors.value = { translations: { message: copy.message, value: swDraft.value.translations } }
			swLanguagesUpgrade.value = copy.upgrade
			track(EVENTS.CAPTIONING_CAP_HIT, { code: err.code, action: 'create', surface: 'dashboard' })
		} else if (isLimitError(err)) {
			// 403 captions_not_included, 429 captions_rate_limited, or a 409 cap.
			const copy = limitErrorCopy(err)
			error.value = copy.message
			errorUpgrade.value = copy.upgrade
			track(EVENTS.CAPTIONING_CAP_HIT, { code: err.code, action: 'create', surface: 'dashboard' })
		} else if (err?.status === 409) {
			error.value = err.message || 'This stream already has a captioning flow.'
		} else if (err?.status === 400) {
			error.value = err.message || 'This stream can\'t be captioned. Pick a live stream you own.'
		} else {
			error.value = err?.message || 'Could not create the captioning flow. Try again.'
		}
	} finally {
		submitting.value = false
	}
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		size="sm"
		title="New captioning"
		wrapper-classes="lg:h-auto lg:max-h-[85vh]"
		scroll-body
		mobile-placement="bottom"
	>
		<div class="flex flex-col gap-4 px-4 pb-4 sm:px-6 sm:pb-6">
			<p v-if="!syncwordsEngine" class="m-0 text-tiny text-surface-8">
				Pick the live stream to caption. Languages and duration are set on the next screen.
			</p>
			<p v-else-if="step === 'stream'" class="m-0 text-tiny text-surface-8" data-testid="create-step">
				<span class="font-medium text-surface-text-1">Step 1 of 2 ·</span> Pick the Castr stream that receives the captioned video.
			</p>
			<p v-else class="m-0 text-tiny text-surface-8" data-testid="create-step">
				<span class="font-medium text-surface-text-1">Step 2 of 2 ·</span> Captions for <span class="font-medium text-surface-text-1">{{ selected?.name }}</span>.
				After you create it, you get an SRT ingest URL for your encoder.
			</p>
			<p v-if="syncwordsEngine && step === 'settings'" class="m-0 text-xs text-surface-7" data-testid="create-account-line">{{ accountLine }}</p>
			<p v-if="syncwordsEngine && step === 'settings' && providerUsageText" class="m-0 text-xs text-surface-7" data-vendor-name data-testid="create-provider-usage">{{ providerUsageText }}</p>

			<template v-if="syncwordsEngine && step === 'settings'">
				<Alert v-if="swUnavailable" type="warning2" role="alert" data-testid="syncwords-unavailable">
					Captioning isn't available right now. Try again later or contact support.
				</Alert>
				<SyncWordsSettingsFields
					v-if="swDraft"
					v-model="swDraft"
					:info="engineInfo"
					:errors="swErrors"
					:disabled="submitting"
					:max-langs="swMaxLangs"
					:plan-capped="swPlanCapped"
					:languages-upgrade="swLanguagesUpgrade && !!swErrors.translations"
					id-prefix="sw-create"
					auto-region
					:auto-region-code="streamRegion"
				/>
				<Alert v-if="error" type="error" role="alert">
					<span>{{ error }}</span>
				</Alert>
			</template>

			<div v-else-if="loadingStreams" class="py-10 text-center text-tiny text-surface-8">Loading your live streams…</div>

			<div v-else-if="liveStreams.length === 0" class="flex flex-col items-center gap-3 rounded-lg bg-surface-3 px-4 py-8 text-center">
				<icon-base width="28" height="28" class="text-surface-7"><icon-camera /></icon-base>
				<p class="m-0 text-sm font-medium text-surface-text-2">You don't have any live streams yet</p>
				<p class="m-0 max-w-xs text-tiny text-surface-8">Captioning attaches to a live stream. Create one first, then come back here.</p>
				<FormButton type="secondary" size="md" to="/dashboard">Go to Livestreams</FormButton>
			</div>

			<template v-else>
				<div class="relative">
					<icon-base class="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-surface-7">
						<icon-search />
					</icon-base>
					<FormInput
						v-model="search"
						type="search"
						placeholder="Search streams"
						aria-label="Search streams"
						input-classes="!pl-9"
					/>
				</div>

				<ul class="captioning-stream-list m-0 flex max-h-[40vh] list-none flex-col gap-1 overflow-y-auto p-0" role="listbox" aria-label="Live streams">
					<li v-for="row in rows" :key="row.key">
						<button
							type="button"
							role="option"
							class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-main"
							:class="[
								row.taken || row.pull ? 'cursor-not-allowed opacity-60' : 'hover:bg-surface-3',
								selectedKey === row.key ? 'bg-surface-3' : 'bg-transparent',
							]"
							:aria-selected="selectedKey === row.key"
							:aria-disabled="row.taken || row.pull"
							:disabled="row.taken || row.pull"
							@click="choose(row)"
						>
							<span
								class="flex size-8 shrink-0 items-center justify-center rounded-md"
								:class="selectedKey === row.key ? 'bg-main text-white' : 'bg-surface-4 text-surface-8'"
								aria-hidden="true"
							>
								<icon-base v-if="selectedKey === row.key" fill="none"><icon-checkmark /></icon-base>
								<icon-base v-else><icon-camera /></icon-base>
							</span>
							<span class="flex min-w-0 flex-1 flex-col">
								<span class="truncate text-sm font-medium text-surface-text-2">{{ row.name }}</span>
								<span class="truncate font-mono text-[11px] text-surface-7">{{ row.key }}</span>
							</span>
							<span v-if="row.taken" class="shrink-0 text-[11px] font-medium text-surface-7">Already has captioning</span>
							<span v-else-if="row.pull" class="shrink-0 text-[11px] font-medium text-surface-7" title="Captioned video is pushed into the stream, so it needs an encoder (push) source">Pull source not supported</span>
							<StatusPill v-else :variant="row.online ? 'success' : 'muted'" size="sm" class="shrink-0">
								{{ row.online ? 'Online' : 'Offline' }}
							</StatusPill>
						</button>
					</li>
					<li v-if="rows.length === 0" class="py-6 text-center text-tiny text-surface-7">
						No live stream matches "{{ search }}".
					</li>
				</ul>

				<Alert type="normal" hide-icon>
					<span class="flex items-start gap-2">
						<icon-base class="mt-0.5 shrink-0 text-surface-8" fill="none"><icon-lock /></icon-base>
						<span>The stream can't be changed after creation. Delete the flow and create a new one to switch streams.</span>
					</span>
				</Alert>

				<Alert v-if="error" type="error" role="alert">
					<span class="flex w-full flex-wrap items-center gap-x-1 gap-y-1">
						<span>{{ error }}</span>
						<router-link v-if="errorUpgrade" :to="UPGRADE_ROUTE" class="font-medium text-white underline" @click="close">Upgrade</router-link>
					</span>
				</Alert>
			</template>
		</div>

		<template #footer>
			<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
				<FormButton
					v-if="syncwordsEngine && step === 'stream' && liveStreams.length > 0"
					type="main"
					size="md"
					class="w-full justify-center sm:w-auto"
					:disabled="!canPick"
					@click="next"
				>
					Next
				</FormButton>
				<FormButton
					v-else-if="liveStreams.length > 0"
					type="main"
					size="md"
					class="w-full justify-center sm:w-auto"
					:disabled="!canCreate"
					:is-loading="submitting"
					@click="create"
				>
					Create captioning
				</FormButton>
				<FormButton v-if="syncwordsEngine && step === 'settings'" type="secondary" size="md" class="w-full justify-center sm:w-auto" :disabled="submitting" @click="back">
					Back
				</FormButton>
				<FormButton v-else type="secondary" size="md" class="w-full justify-center sm:w-auto" @click="close">
					Cancel
				</FormButton>
			</footer>
		</template>
	</ModalBox>
</template>
